import { Component, HostBinding, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MessageService, MessageType } from '@mono-repo/messages';
import { TranslateService, UtilityService } from '@mono-repo/util';
import { environment } from '../../environments_mc/environment';
import { LoadingIndicatorService } from '@mono-repo/loader';
import { CordovaFingerprintAioPluginService } from '@mono-repo/plugins';
import { OdataService, OdataOperation } from '@mono-repo/odata';
import { PageContainer } from '../cockpit/_shared/page-container/page-container';
import { AppService } from '../app.service';
import { HeaderService } from '../cockpit/_shared/header/header.service';
import { SystemEnvironment } from '@mono-repo/data';
import { ConfigService } from '../config.service';

@Component({
  selector: 'app-set-password',
  templateUrl: './unlock.component.html',
})
export class UnlockComponent extends PageContainer implements OnInit {
  @HostBinding('class') class = 'dt-c-unlock';

  /***************************************************************************
   * component variables
   **************************************************************************/
  public unlockForm = new FormGroup({
    userPassword: new FormControl('', Validators.required),
  });
  public pageSubheading: string;
  public unlockWithFingerprint: boolean;
  public biometricAvailable = false;
  public isLoading = false;
  public maxWidth: string = this.configService.getConfig().loginLogoMaxWidth;
  public applicationName: string = environment.applicationName;
  public showPassword = false;
  @ViewChild('inputPassword') inputPasswordRef: ElementRef;
  @ViewChild('iconTooglePassword') iconTooglePasswordRef: ElementRef;
  @ViewChild('buttonTooglePassword') buttonTooglePasswordRef: ElementRef;

  constructor(
    private readonly odataService: OdataService,
    private router: Router,
    private messageService: MessageService,
    private loaderService: LoadingIndicatorService,
    private utilityService: UtilityService,
    private appService: AppService,
    private readonly configService: ConfigService,
    private biometricsService: CordovaFingerprintAioPluginService,
    headerService: HeaderService,
    translateService: TranslateService,
  ) {
    super(headerService, translateService);
    if (environment.systemEnvironment === SystemEnvironment.LOCALHOST) {
      localStorage.setItem('isUnlocked', 'true');
      //this.router.navigateByUrl(this.utilityService.getSourceUrl());
    }
    this.unlockWithFingerprint = this.configService.getConfig().unlockWithFingerprint;
    this.setTranslateModule('unlock');
    this.pageSubheading = this.translateService.translate('PAGE_SUBHEADING');
  }

  ngOnInit() {
    if (this.unlockWithFingerprint) {
      setTimeout(() => {
        this.unlockWithBiometric();
      }, 1);
    }
  }

  public requestPassword(): void {
    this.router.navigateByUrl('/requestpassword');
  }

  public unlockWithPassword(): void {
    this.loaderService.show();
    this.odataService
      .post<void>(new OdataOperation().Query('checkPassword'), { passwordOld: this.unlockForm.value.userPassword })
      .subscribe(
        () => {
          localStorage.setItem('isUnlocked', 'true');
          this.loaderService.hide();
          this.router.navigateByUrl(this.utilityService.getSourceUrl());
        },
        () => {
          this.messageService.add(this.translateService.translate('WRONG_PASSWORD'), MessageType.ERROR);
          this.loaderService.hide();
        },
      );
  }

  public unlockWithBiometric(): void {
    this.loaderService.show();
    this.biometricsService.isAvailable().subscribe(
      () => {
        this.biometricAvailable = true;
        return this.biometricsService
          .triggerBiometrics({
            title: environment.name,
            description: this.translateService.translate('UNLOCK_WITH_BIOMETRIC'),
            disableBackup: true,
            fallbackButtonTitle: this.translateService.translate('CANCEL'),
            cancelButtonTitle: this.translateService.translate('CANCEL'),
          })
          .subscribe(
            () => {
              this.isLoading = true;
              localStorage.setItem('isUnlocked', 'true');
              this.loaderService.hide();
              this.router.navigateByUrl(this.utilityService.getSourceUrl());
            },
            (error) => {
              // since we use different plugins on iOS and android and these behave differently
              // we might not be able to distinguish an abort and an error
              // here we are just assuming the user aborted and silently fail as otherwise we might give a warning on a valid action
              console.warn('the biometric unlock operation failed', error);
              this.handleErrorFromTrigger(error);
              this.loaderService.hide();
            },
          );
      },
      (error) => {
        this.handleError(error);
        this.biometricAvailable = false;
        this.loaderService.hide();
      },
    );
  }

  private handleError(errorCode) {
    if (errorCode === -111 || errorCode === -100) {
      switch (window['device'].platform) {
        case 'iOS':
          this.biometricAvailable = false;
          this.messageService.add(this.translateService.translate('FINGERPRINT_LOCKED_IOS'), MessageType.WARNING);
          break;

        case 'Android':
          this.messageService.add(this.translateService.translate('FINGERPRINT_LOCKED_ANDROID'), MessageType.WARNING);
          break;

        default:
          this.messageService.add(this.translateService.translate('FINGERPRINT_LOCKED'), MessageType.WARNING);
      }
    }
  }
  private handleErrorFromTrigger(errorCode) {
    if (errorCode === -111) {
      switch (window['device'].platform) {
        case 'iOS':
          this.biometricAvailable = false;
          this.messageService.add(this.translateService.translate('FINGERPRINT_LOCKED_IOS'), MessageType.WARNING);
          break;

        case 'Android':
          this.messageService.add(this.translateService.translate('FINGERPRINT_LOCKED_ANDROID'), MessageType.WARNING);
          break;

        default:
          this.messageService.add(this.translateService.translate('FINGERPRINT_LOCKED'), MessageType.WARNING);
      }
    }
  }
  public logout(): void {
    this.appService.logout();
  }

  public togglePassword(): void {
    this.showPassword = !this.showPassword;
    if (this.showPassword) {
      this.inputPasswordRef.nativeElement.type = 'text';
      this.iconTooglePasswordRef.nativeElement.classList.remove('ui-icon-eye');
      this.iconTooglePasswordRef.nativeElement.classList.add('ui-icon-eye-deny');
      this.buttonTooglePasswordRef.nativeElement.ariaLabel = this.translateService.translate('UI_HIDEPASSWORD');
    } else {
      this.inputPasswordRef.nativeElement.type = 'password';
      this.iconTooglePasswordRef.nativeElement.classList.remove('ui-icon-eye-deny');
      this.iconTooglePasswordRef.nativeElement.classList.add('ui-icon-eye');
      this.buttonTooglePasswordRef.nativeElement.ariaLabel = this.translateService.translate('UI_SHOWPASSWORD');
    }
  }
}
